import { z } from 'zod';

/**
 * THE GAME SURFACE a player can actually see.
 *
 * `game.game`, `game.game_provider` and `game.game_brand` have been populated
 * since the seed. Nothing player-facing read them: the portal's games screen was
 * an honest empty state, and the twelve titles the design mock listed — real
 * third-party games with invented RTPs — were removed in the truth pass rather
 * than wired to anything.
 *
 * This file is the vocabulary the API, the player portal and the tests share, so
 * that "which categories exist", "why a title cannot be opened" and "what a
 * launch may contain" have exactly one definition each.
 *
 * ── WHY THE CATEGORY SET IS CLOSED ──────────────────────────────────────────
 *
 * `category` is a caller-chosen filter and reaches a WHERE clause. It is
 * therefore matched against a CLOSED SET before it goes anywhere near the
 * database — the same pattern `services/api/src/modules/partner-access/routes.ts`
 * and `packages/reports/src/model.ts` use for caller-chosen columns. A category
 * that is not in this list is refused with a message naming what is, rather than
 * silently returning nothing (which reads to a player as "there are no slots").
 *
 * The seven values are `game.game`'s own documented set — see the `category`
 * column's comment in `packages/db/src/schema/game.ts`.
 */
export const GAME_CATEGORIES = [
  'SLOTS',
  'LIVE_CASINO',
  'CRASH',
  'ARCADE',
  'FISHING',
  'BINGO',
  'TABLE',
] as const;

export const gameCategory = z.enum(GAME_CATEGORIES);
export type GameCategory = z.infer<typeof gameCategory>;

/** How each category is said on screen. The column stores the left-hand side. */
export const GAME_CATEGORY_LABELS: Record<GameCategory, string> = {
  SLOTS: 'Slots',
  LIVE_CASINO: 'Live casino',
  CRASH: 'Crash',
  ARCADE: 'Arcade',
  FISHING: 'Fishing',
  BINGO: 'Bingo',
  TABLE: 'Table games',
};

/**
 * A category's label, for a value that came out of the DATABASE.
 *
 * Falls back to the raw code, spaced and sentence-cased, rather than to "Other".
 * A category this list has not caught up with must still be NAMED — a player
 * filtering by "Fishing" needs to see the word, and an engineer needs to see
 * which value the database is holding that the code does not know about.
 * Hiding it behind "Other" is how a whole shelf of games goes missing quietly.
 */
export function gameCategoryLabel(code: string): string {
  const known = (GAME_CATEGORY_LABELS as Record<string, string | undefined>)[code];
  if (known) return known;
  const words = code.toLowerCase().split('_');
  const [first, ...rest] = words;
  if (first === undefined) return code;
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ');
}

/**
 * The provider's own game type, said the way a player would say it.
 *
 * SA sends these as their own short codes — `bac`, `rot`, `dtx` — and every one
 * of their 41 tables is LIVE_CASINO at the top level. So this second axis is
 * what lets somebody tell Baccarat from Sic Bo on a shelf where the category
 * chip says the same thing for all of them.
 *
 * Same rule as `gameCategoryLabel`: an unknown code is NAMED, not swallowed.
 * SA adds table types without telling us, and a type this map has not caught up
 * with must still appear as a word rather than vanish into "Other" — otherwise
 * a whole group of tables goes missing from the shelf quietly, which is how
 * nobody notices for a month.
 */
export const GAME_SUB_CATEGORY_LABELS: Record<string, string> = {
  bac: 'Baccarat',
  rot: 'Roulette',
  ultraroulette: 'Ultra Roulette',
  dtx: 'Dragon Tiger',
  sicbo: 'Sic Bo',
  pokdeng: 'Pok Deng',
  thaihilo: 'Thai Hi-Lo',
  xocdia: 'Xoc Dia',
  fishprawncrab: 'Fish Prawn Crab',
  boundlessblackjack: 'Boundless Blackjack',
  deluxeblackjack: 'Deluxe Blackjack',
};

export function gameSubCategoryLabel(code: string): string {
  const known = GAME_SUB_CATEGORY_LABELS[code.toLowerCase()];
  if (known) return known;
  /* Unknown: show the provider's own word, capitalised. Not "Other". */
  const raw = code.trim();
  if (raw === '') return code;
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/* ══════════════════════════════════════════════════════════════════════════
   WHY A TITLE CANNOT BE OPENED
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * The closed set of reasons a launch is refused, and the sentence each one is.
 *
 * Shared rather than written twice because the SAME reason has to appear in two
 * places that must not disagree: on the tile BEFORE a player taps it, so the
 * control states its true reason, and in the refusal the launch route returns
 * if they tap anyway. Two copies of that sentence is two chances for the tile
 * to promise something the route then refuses.
 *
 * PROVIDER_NOT_INTEGRATED is the one that is true of every game in this build.
 * SA and JILI carry their real drivers; the three other seed providers carry
 * `driver = 'MOCK'`, and a mock driver has no game behind it: there is nothing to open, in any environment.
 * Saying that plainly is the whole point — a Play button that opens nothing is
 * the dishonesty this codebase exists not to have.
 */
export const GAME_LAUNCH_REFUSALS = [
  'PROVIDER_NOT_INTEGRATED',
  'PROVIDER_NOT_LIVE',
  'GAME_NOT_LIVE',
  'DRIVER_NOT_IMPLEMENTED',
  /* The provider is live but does not list this title under this code: a seed
     placeholder, or a catalogue row written by hand. */
  'GAME_NOT_AT_PROVIDER',
  /* A LIVE provider's own per-request answer, not our catalogue state: the
     provider refused this login (maintenance, a locked account at its end, a
     bad key) or could not be reached in time. SA Gaming's SA-12/SA-13. */
  'PROVIDER_UNAVAILABLE',
  /* The PLAYER may not play right now (suspended, self-excluded, or staff
     restricted play). Deliberately neutral: which one applies is never said
     (god's ruling OQ-SA-16). */
  'PLAYER_BARRED',
  /*
   * The account has not been verified yet — PAGCOR item 13, and the operator's
   * decision that an account does nothing until a person's identity is confirmed.
   *
   * SEPARATE FROM `PLAYER_BARRED`, AND THE REASON IS WORTH SAYING. That code is
   * neutral because a suspended or self-excluded player must not be told which
   * applies: one is a sanction and the other is a choice they may be trying to
   * work around. Neither is an action they can take right now.
   *
   * This one IS. The player can finish verifying, they can already read their own
   * state from `GET /api/player/identity`, and withholding it would be withholding
   * the single thing they can do about the refusal in front of them. Telling them
   * leaks nothing: it is their own account's state, said back to them.
   *
   * Sent for an account with no check yet. A check in progress and a refused one
   * have codes of their own, below.
   */
  'IDENTITY_NOT_VERIFIED',
  /*
   * The account's last identity check was REFUSED by a person. Its own code, the
   * one a refused deposit, withdrawal and payout account carry too
   * (`IDENTITY_REJECTED_REFUSAL`): the identity screen says what the player may do
   * next — send new photos, or reach support — so this names neither, and never the
   * reason. It answered IDENTITY_NOT_VERIFIED before, which sent a refused player to
   * verify as if no check had ever been made.
   */
  'IDENTITY_REJECTED',
  /*
   * The account's check is IN PROGRESS: the photographs are in, and it waits on the
   * identity service, on a person — every check after a refusal goes to one — or
   * on the player's own live photo. The code a refused deposit, withdrawal and
   * payout account carry for the same wait (`IDENTITY_CHECK_IN_PROGRESS_REFUSAL`).
   * It answered IDENTITY_NOT_VERIFIED before, whose sentence asks for a photo of the
   * ID and the face, as if nothing had been sent.
   */
  'IDENTITY_CHECK_IN_PROGRESS',
] as const;

export const gameLaunchRefusal = z.enum(GAME_LAUNCH_REFUSALS);
export type GameLaunchRefusal = z.infer<typeof gameLaunchRefusal>;

export interface GameLaunchSubject {
  /** The game's name, as a player reads it. */
  game: string;
  /** The provider's name. */
  provider: string;
}

/**
 * The sentence a refusal is, with the names filled in.
 *
 * Written to a player, not to an operator: it says what did NOT happen (no
 * stake, no round) as well as why, because the first question somebody has when
 * a casino screen refuses them is whether their money moved.
 */
export function gameLaunchRefusalSentence(
  code: GameLaunchRefusal,
  { game, provider }: GameLaunchSubject,
): string {
  switch (code) {
    case 'PROVIDER_NOT_INTEGRATED':
      return (
        `${provider} is connected to this platform in simulation only, so there is no ` +
        `${game} to open. Nothing was staked and no round was started.`
      );
    case 'PROVIDER_NOT_LIVE':
      return (
        `${provider} is not live at the moment, and a provider that is not live blocks every ` +
        `game on its shelf. ${game} cannot be opened until it is back. Nothing was staked.`
      );
    case 'GAME_NOT_LIVE':
      return (
        `${game} is not live at the moment — the platform has it flagged rather than open. ` +
        'Nothing was staked and no round was started.'
      );
    case 'DRIVER_NOT_IMPLEMENTED':
      return (
        `${provider} is configured for a live connection that this build of the platform does ` +
        `not implement, so ${game} cannot be opened. Nothing was staked.`
      );
    case 'GAME_NOT_AT_PROVIDER':
      return `${provider} does not list ${game} under this code, so it cannot be opened. Nothing was staked.`;
    case 'PLAYER_BARRED':
      return 'Games are not available on your account right now. Nothing was staked.';
    /* None of the three says how long a check takes: whether a person or the identity service reads
       it is not the launch's to know. The identity screen says it, from the platform's facts. */
    case 'IDENTITY_NOT_VERIFIED':
      return (
        'Your identity has to be verified before you can play. Open "Verify your identity" on ' +
        'your profile — it takes a photo of your ID and your face. Nothing was staked.'
      );
    case 'IDENTITY_REJECTED':
      return (
        'Games are closed because your last identity check was not accepted. "Verify your ' +
        'identity" says what you can do next. Nothing was staked.'
      );
    /* Never that the check's end opens games: play-gate answers a check in progress before it
       asks about a self-exclusion or a play restriction, and for a player under either it opens
       nothing. */
    case 'IDENTITY_CHECK_IN_PROGRESS':
      return (
        'Games stay closed while your identity check is under way. "Verify your identity" shows ' +
        'where your check is, and whether anything is still needed from you. Nothing was staked.'
      );
    case 'PROVIDER_UNAVAILABLE':
      return (
        `${provider} did not open ${game} just now — it is unavailable or did not answer in time. ` +
        'Nothing was staked. Try again in a moment.'
      );
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   REQUESTS
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * The catalogue query.
 *
 * `category` only. There is no search parameter and no provider parameter,
 * because neither control exists yet on the screen and a query field nothing
 * sends is a field nothing tests.
 */
export const playerGameQuery = z.object({
  category: gameCategory.optional(),
});
export type PlayerGameQuery = z.infer<typeof playerGameQuery>;

/**
 * A launch.
 *
 * `idempotencyKey` is the same discipline `game.provider_transaction` carries in
 * its `(provider_id, external_id, operation)` unique key: a retried call must
 * return the STORED result rather than execute again. A player on a phone taps
 * twice, or the connection drops after the request left and the app resends —
 * neither may open a second session against the same game.
 *
 * OPTIONAL, because a caller that supplies none is asking for a genuinely new
 * attempt and gets one. What is not offered is a key chosen by the SERVER from
 * a time bucket: that would silently collapse two deliberate launches a second
 * apart into one, which is a different lie.
 */
export const launchGameRequest = z.strictObject({
  idempotencyKey: z.uuid('An idempotency key must be a UUID.').optional(),
  /* Hints for the provider's client (RA SA-11, SA-123). The device is the
     portal's own reading of the screen; neither hint is trusted for anything
     but presentation. A return address is NOT accepted from the client — the
     server builds it from the brand, so a launch cannot become an open
     redirect. */
  device: z.enum(['MOBILE', 'DESKTOP']).optional(),
  language: z.enum(['en', 'fil']).optional(),
});
export type LaunchGameRequest = z.infer<typeof launchGameRequest>;

/* ══════════════════════════════════════════════════════════════════════════
   RESPONSES — the shape of what the database actually holds
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * One tile.
 *
 * EVERY FIELD IS A COLUMN. There is no `rtp` invented for display, no `hot`, no
 * `players online`, no `thumbnail` — `game.game` holds no image reference of any
 * kind and there is no asset pipeline in this workspace, so `iconName` is the
 * only art the platform can name and the tile falls back to type when it cannot
 * resolve one.
 *
 * `declaredRtpBp` is the provider's declared figure from
 * `game.game.declared_rtp_bp`, in basis points, as a STRING for the same reason
 * money is: it is a database integer and JSON numbers are doubles. It is null
 * wherever the platform holds no figure, and a null renders as nothing at all —
 * never as a dash that reads like a value, and never as a number this platform
 * measured, which it has not: measured RTP needs rounds, and there are none.
 */
export interface PlayerGameSummary {
  code: string;
  name: string;
  category: string;
  categoryLabel: string;
  /** LIVE · MAINTENANCE. A DISABLED game never reaches a player. */
  status: string;
  /** `game.game.icon_name`, or null. Not an image — see the tile component. */
  iconName: string | null;
  /**
   * The tile's picture: one an operator uploaded, else the provider's, else null.
   *
   * Unlike `iconName` this really is an image. Null is the ordinary case for
   * the five simulated providers and is not an error: the tile draws its
   * monogram instead, which is a real picture rather than a broken one.
   */
  iconUrl: string | null;
  /**
   * The provider's own game type (SA's `bac`, `rot`, `dtx`, ...), or null.
   * A second grouping axis: every SA table is LIVE_CASINO, so this is the only
   * thing that separates Baccarat from Sic Bo on the shelf.
   */
  subCategory: string | null;
  /** `subCategory` said the way a player would say it. Null when there is none. */
  subCategoryLabel: string | null;
  /** Basis points as a string: "9640" is 96.40%. Null when none is held. */
  declaredRtpBp: string | null;
  provider: { code: string; name: string; status: string };
  /** Whether POST …/launch would open anything. False for every game today. */
  launchable: boolean;
  /** Why not, stated before the player taps. Null only when launchable. */
  refusal: { code: GameLaunchRefusal; message: string } | null;
  /**
   * WHAT OPENING THIS ACTUALLY DOES, when it is not simply this title.
   *
   * SA Gaming's launch URL carries a username, a token and a lobby code, and
   * NOTHING that names a table (their §5.2.1). So every one of SA's 41 tables
   * opens the same lobby, where the player then picks a table themselves.
   *
   * Without this sentence the shelf shows forty-one distinct tiles that all do
   * the same thing — a screen making a promise the launch cannot keep. Null
   * when opening a title does exactly what its name says.
   */
  launchNote: string | null;
}

/** A category chip. The count is the catalogue's own, not a target. */
export interface PlayerGameCategoryFacet {
  category: string;
  label: string;
  games: number;
}

export interface PlayerGameCatalogue {
  brand: { ref: string; name: string };
  /** Null when no category filter was applied. */
  category: string | null;
  categories: PlayerGameCategoryFacet[];
  items: PlayerGameSummary[];
  /** How many titles are open right now. Zero, in this build, and said so. */
  launchableCount: number;
}

/** One recorded launch attempt, as the player's own history reads it. */
export interface PlayerGameLaunchRecord {
  gameCode: string;
  gameName: string;
  providerName: string;
  /** LAUNCHED · REFUSED. */
  outcome: string;
  refusal: { code: string; message: string } | null;
  /** ISO instant. Render with gamingDate/gamingTime — never toLocaleDateString. */
  startedAt: string;
  /** ISO instant, or null while a session is still open. */
  endedAt: string | null;
}

/**
 * ONE ROUND THE PLAYER PLAYED, as their own history reads it.
 *
 * ── WHY THIS IS NOT THE LAUNCH LOG, AND NOT THE MONEY LEDGER ──────────────
 *
 * There are three records of a player gambling and they answer three different
 * questions. `PlayerGameLaunchRecord` above is what they ASKED to open and
 * whether the platform let them; the wallet ledger is what MOVED, in pesos; this
 * is what they PLAYED.
 *
 * A player opening "history" means this one. The ledger already shows BET and
 * WIN rows and cannot name the game beside them — `game.game_round` has no link
 * to a wallet transaction and those transactions carry no external reference, so
 * the money and the round cannot be joined. The history screen says so rather
 * than guessing. This is the other half: the round, with its game, which the
 * ledger will never be able to name.
 *
 * THE TWO LISTS DO NOT RECONCILE ROW BY ROW and nothing should imply they do.
 * A screen that put them side by side under one heading would be inviting
 * somebody to match a ₱50 bet against a ₱50 debit, which is a coincidence rather
 * than a link.
 *
 * ── WHAT IS SHOWN THAT A PLAYER MIGHT NOT EXPECT ──────────────────────────
 *
 * `voided` rounds ARE returned, marked. A round that vanished from a player's
 * own history would read as money taken; the honest answer is that the provider
 * cancelled it and the stake came back.
 *
 * `bonusFunded` rounds are returned and marked too. It is their play either way,
 * and the distinction matters to them: a win on bonus money carries wagering
 * before it can be withdrawn, and a player totalling their own stake would
 * otherwise count money that was never theirs to lose.
 */
export interface PlayerGameRound {
  /** The public reference, which support can look up. */
  ref: string;
  gameName: string;
  providerName: string;
  /** Centavos, as a string — never a JS number for money. */
  stakeMinor: string;
  returnedMinor: string;
  /** `returned - stake`. Negative on a loss, and the screen says which. */
  netMinor: string;
  /** Cancelled by the provider: the stake came back and nothing counts. */
  voided: boolean;
  /** Played with bonus money, so a win carries wagering before withdrawal. */
  bonusFunded: boolean;
  /** ISO instant. Render with gamingDate/gamingTime — never toLocaleDateString. */
  settledAt: string;
}

/**
 * What the player staked and got back over THE WHOLE WINDOW, not the page.
 *
 * Summed by the server for the reason the wallet ledger's totals are: a figure
 * added up from the twenty visible rows changes every time somebody presses
 * Older, which on anything about money is worse than showing no figure at all.
 *
 * Split into staked and returned rather than netted, because "I staked ₱5,000
 * and got back ₱4,800" is two facts a player can check, and "you are ₱200 down"
 * is one they have to take on trust.
 *
 * VOIDED ROUNDS ARE IN NEITHER TOTAL. The stake came back and the round counts
 * for nothing, so including it would inflate both sides by the same amount and
 * overstate how much somebody has played.
 */
export interface PlayerPlayTotals {
  stakedMinor: string;
  returnedMinor: string;
  /** Rounds in the window, voided ones included — they are in the list. */
  rounds: number;
}

/**
 * One recent win, as the lobby's community feed shows it.
 *
 * ── IT CARRIES NO IDENTIFIER AT ALL, AND THAT IS THE SECOND ANSWER ────────
 *
 * The first version published a partially masked player reference —
 * `PL-103••` rather than `PL-10388` — on the reasoning that removing two
 * characters put roughly a hundred accounts behind every row. **Measured against
 * the real table, that was false.** Grouping every player by their masked prefix
 * gave 31 groups whose sizes ran from 1 to 100: the bulk accounts sat in groups
 * of ninety-nine, and **seven prefixes identified exactly one player.** The
 * named, early-registered accounts are precisely the sparse ones, so for those
 * players the "mask" published their reference in full.
 *
 * Masking a fixed number of characters gives a privacy guarantee that depends on
 * how densely the reference space happens to be populated, which is not a
 * property anybody maintains and not one the next reader would think to check.
 * So no identifier is published. A row is a game, an amount and a day.
 *
 * Nothing is lost. The reference could not have been recognised by its own owner
 * anyway — ninety-nine other accounts shared it — and "Super Ace · ₱298.46
 * · 30 July" is the whole of what a winners feed needs to say.
 *
 * ── `wonMinor` IS THE NET, NOT THE PAYOUT ─────────────────────────────────
 *
 * `win_amount` on a round is the PAYOUT, and a payout smaller than the stake is
 * a loss. On the development data 60,886 rounds have a payout and only 11,250
 * are net wins — so a feed keyed on `win_amount > 0` would have listed people
 * who lost money, under a heading saying they won. This is the stake taken off:
 * what the player actually gained.
 */
export interface PlayerWinnerRow {
  /** The title. Shown because a feed with no game names tells a player nothing. */
  gameName: string;
  /** Centavos as a decimal string: payout minus stake, always positive. */
  wonMinor: string;
  /**
   * The GAMING DAY the win settled on, `YYYY-MM-DD` in Asia/Manila, already
   * resolved by the database.
   *
   * A day rather than an instant, deliberately: a timestamp to the millisecond
   * is a fingerprint, and it is precision this screen never renders. It is also
   * a string rather than a date so that nothing calls `toISOString()` on it and
   * reads the day before — the defect that faked a mismatch on every identity
   * review case.
   */
  wonOn: string;
}

export interface PlayerWinners {
  /** Biggest first. At most six. */
  winners: PlayerWinnerRow[];
  /** How far back the feed looked, in days — so the screen can say so. */
  windowDays: number;
}
